import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorPage } from '../Components/Custom/ErrorPage';
import { setToken } from '../utils/TokenUtils';

/**
 * Where front-porch sends a manager who asked to view a homeowner's
 * portal.
 *
 * **The token arrives in the URL fragment, and that is deliberate.** The
 * two apps are different origins, so the token has to cross somehow, and
 * a fragment is the one part of a URL that browsers never send to a
 * server: it appears in no access log, no proxy trace and no `Referer`.
 * It is the same reason OAuth's implicit flow used one.
 *
 * What a fragment does *not* escape is the address bar and this browser's
 * own history, which is why the effect below strips it immediately and
 * replaces the entry rather than pushing one. The residual exposure is a
 * short-lived, read-only, audited token on the manager's own machine.
 *
 * Rejected: a query string, which lands in server logs the moment
 * anything proxies this app; and `postMessage`, which avoids the URL
 * entirely but needs both windows alive and co-operating, and fails in a
 * way that looks like nothing happening.
 */
const ImpersonationLanding: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  // React 18 mounts effects twice in development. The second run would
  // find the fragment already stripped and report a failure over a
  // session that worked.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    // `window.location.hash` keeps its leading `#`.
    const fragment = new URLSearchParams(
      window.location.hash.replace(/^#/, '')
    );
    const token = fragment.get('token');

    // Strip it before anything else can read it, and **replace** the
    // history entry rather than adding one, so Back does not return to a
    // URL containing a token.
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}`
    );

    if (!token) {
      setError(
        'This link did not carry a session. Start the view again from the ' +
          "homeowner's page in the manager portal."
      );
      return;
    }

    // `false` — not "remember me". An impersonated view is deliberately
    // not durable: it lasts an hour, cannot be refreshed, and should not
    // outlive the tab it was opened in. sessionStorage is exactly that.
    setToken(token, false);

    navigate('/home', { replace: true });
  }, [navigate]);

  if (error) {
    // No "Back to home": there is no session here to go home with.
    return (
      <ErrorPage
        title="Couldn't open this view"
        message={error}
        homeHref={null}
      />
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-gray-600 dark:text-gray-300">Opening…</p>
    </div>
  );
};

export default ImpersonationLanding;
